import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import firebaseConfig from "../fb.json";
import apps from "../applications.json";
import "../styles/validation.css";

// On-device/simulator UI validation galleries (wrkfl-mfrys). The ai-mobile-apps
// CI harness (helpers/run-ios-validate.sh) walks each app's onboarding → paywall,
// screenshots every screen, and publishes a per-run history under the public
// builds/_validation/ prefix:
//   builds/_validation/apps.json          — apps that have a gallery
//   builds/_validation/<app>/index.json   — run history (newest first)
// We read those JSON manifests directly (public read) — no Firebase SDK needed.

const BUCKET = (firebaseConfig as any).storageBucket as string;

const dl = (path: string) =>
  `https://firebasestorage.googleapis.com/v0/b/${BUCKET}/o/${encodeURIComponent(
    path
  )}?alt=media&t=${Date.now()}`;

interface ValidationRun {
  app: string;
  runId: string;
  branch: string;
  channel: string;
  ts: string;
  reached_paywall: boolean;
  shots: string[];
}

const titleFor = (name: string) =>
  (apps as { name: string; title: string }[]).find((a) => a.name === name)
    ?.title ?? name;

const fmtTs = (ts: string) => {
  const d = new Date(ts);
  return isNaN(d.getTime()) ? ts : d.toLocaleString();
};

const Validation = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [appNames, setAppNames] = useState<string[] | null>(null);
  const [runs, setRuns] = useState<ValidationRun[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<string | null>(null);

  const selectedApp = searchParams.get("app");
  const selectedRunId = searchParams.get("run");

  // Load the app list (apps that have a validation gallery).
  useEffect(() => {
    fetch(dl("builds/_validation/apps.json"))
      .then((r) => (r.ok ? r.json() : []))
      .then((list: string[]) => setAppNames(Array.isArray(list) ? list : []))
      .catch(() => setAppNames([]));
  }, []);

  // Load run history for the selected app.
  useEffect(() => {
    if (!selectedApp) {
      setRuns(null);
      return;
    }
    setRuns(null);
    setError(null);
    fetch(dl(`builds/_validation/${selectedApp}/index.json`))
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((list: ValidationRun[]) =>
        setRuns(Array.isArray(list) ? list : [])
      )
      .catch(() => setError("No runs found for this app."));
  }, [selectedApp]);

  const selectedRun = useMemo(() => {
    if (!runs || runs.length === 0) return null;
    return runs.find((r) => r.runId === selectedRunId) ?? runs[0];
  }, [runs, selectedRunId]);

  const pick = (next: Record<string, string>) => {
    const p = new URLSearchParams(searchParams);
    Object.entries(next).forEach(([k, v]) => p.set(k, v));
    setSearchParams(p);
  };

  return (
    <div className="validation">
      <div className="validation__header">
        <h1 className="validation__title">Validation screenshots</h1>
        <p className="validation__subtitle">
          Automated onboarding → paywall walk-throughs, per app build.
        </p>
      </div>

      {appNames === null ? (
        <p className="validation__muted">Loading…</p>
      ) : appNames.length === 0 ? (
        <p className="validation__muted">No validated apps yet.</p>
      ) : (
        <div className="validation__apps">
          {appNames.map((name) => (
            <button
              key={name}
              className={`validation__app ${
                selectedApp === name ? "validation__app--active" : ""
              }`}
              onClick={() => pick({ app: name })}
            >
              {titleFor(name)}
            </button>
          ))}
        </div>
      )}

      {selectedApp && (
        <div className="validation__body">
          {error && <p className="validation__muted">{error}</p>}

          {runs && runs.length > 0 && (
            <div className="validation__runbar">
              <label className="validation__runlabel">Run</label>
              <select
                className="validation__select"
                value={selectedRun?.runId ?? ""}
                onChange={(e) => pick({ run: e.target.value })}
              >
                {runs.map((r) => (
                  <option key={r.runId} value={r.runId}>
                    {fmtTs(r.ts)} · {r.branch} · {r.shots.length} shots
                    {r.reached_paywall ? " ✓" : " ✕"}
                  </option>
                ))}
              </select>

              {selectedRun && (
                <span
                  className={`validation__badge ${
                    selectedRun.reached_paywall
                      ? "validation__badge--ok"
                      : "validation__badge--warn"
                  }`}
                >
                  {selectedRun.reached_paywall
                    ? "reached paywall"
                    : "paywall not reached"}
                </span>
              )}
            </div>
          )}

          {selectedRun && (
            <div className="validation__grid">
              {selectedRun.shots.map((src, i) => (
                <figure className="validation__shot" key={src}>
                  <img
                    src={src}
                    alt={`${selectedApp} step ${i + 1}`}
                    loading="lazy"
                    onClick={() => setLightbox(src)}
                  />
                  <figcaption>{i + 1}</figcaption>
                </figure>
              ))}
              {selectedRun.shots.length === 0 && (
                <p className="validation__muted">This run has no screenshots.</p>
              )}
            </div>
          )}
        </div>
      )}

      {lightbox && (
        <div className="validation__lightbox" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="" />
        </div>
      )}
    </div>
  );
};

export default Validation;
